import {
  authJwtCreateCreate,
  authJwtRefreshCreate,
  authUsersResetPasswordConfirmCreate,
  authUsersResetPasswordCreate,
} from "@sgdf/client";
import type { MeOut } from "@sgdf/client-v2";
import { retrieveMe } from "@sgdf/client-v2";
import { configureApiClient } from "../configuration";
import { DateTime } from "luxon";
import { jwtDecode } from "jwt-decode";

// Cache local de l'utilisateur courant. La clé porte la version du contrat :
// un cache de l'API v1 (``participations[].meeting``) encore présent après une
// mise en production serait lu comme une forme v2 (``meeting_id``) pendant
// jusqu'à 12 h. ``LEGACY_USER_KEY`` est purgée à la première écriture.
const USER_KEY = "current_user_v2";
const LEGACY_USER_KEY = "current_user";

// When several requests fire in parallel and the access token is expired, each
// interceptor invocation would otherwise call `refreshToken` independently. With
// rotating refresh tokens that races: only the first call succeeds and the rest
// fail with a now-invalid refresh token. Share a single in-flight promise so
// concurrent callers await the same refresh.
let inFlightRefresh: Promise<{
  access: string;
  refresh?: string;
} | null> | null = null;

let sessionCleanup: (() => void) | undefined;
let sessionVersion = 0;

export const getSessionVersion = () => sessionVersion;

/** Le plugin Vue Query fournit la purge des stores de l'application. */
export const registerSessionCleanup = (cleanup: () => void) => {
  sessionCleanup = cleanup;
};

export const authenticationService = {
  login: async (user: { username: string; password: string }) => {
    const { data, error } = await authJwtCreateCreate({
      body: {
        username_or_email: user.username,
        password: user.password,
      },
    });

    if (error || !data) {
      return Promise.reject({
        non_field_errors: ["message.user_not_authorized_backoffice"],
      });
    }

    const { access, refresh } = data;
    if (access && refresh) {
      authenticationService.logout();
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      configureApiClient();
    } else {
      return Promise.reject({
        non_field_errors: ["message.user_not_authorized_backoffice"],
      });
    }
  },

  logout: (meetingId?: string) => {
    sessionVersion++;
    authenticationService.invalidateUserCache();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    if (meetingId) {
      localStorage.removeItem(`qrcode_token:${meetingId}`);
    }

    localStorage.removeItem("current_meeting");
    inFlightRefresh = null;
    sessionCleanup?.();
  },

  invalidateUserCache: () => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem("last_user_update");
  },

  resetPassword: async (email: string) => {
    const { error } = await authUsersResetPasswordCreate({
      body: { email },
    });
    if (error) throw error;
  },

  resetPasswordConfirm: async (
    uid: string,
    token: string,
    newPassword: string,
    reNewPassword: string,
  ) => {
    const { error } = await authUsersResetPasswordConfirmCreate({
      body: {
        uid,
        token,
        new_password: newPassword,
        re_new_password: reNewPassword,
      },
    });
    if (error) throw error;
  },

  scanCode: ({
    qrCodeToken,
    meetingId,
  }: {
    qrCodeToken: string;
    meetingId: string;
  }) => {
    localStorage.setItem(`qrcode_token:${meetingId}`, qrCodeToken);
    localStorage.setItem("current_meeting", meetingId);
  },

  refreshToken: async () => {
    if (inFlightRefresh) return inFlightRefresh;

    const pending = (async () => {
      const refresh_token = localStorage.getItem("refresh_token");
      if (!refresh_token) return null;

      try {
        const { data } = await authJwtRefreshCreate({
          body: { refresh: refresh_token },
        });

        // Une réponse de l'ancienne session peut arriver après logout/login.
        if (!data || localStorage.getItem("refresh_token") !== refresh_token) return null;

        localStorage.setItem("access_token", data.access);
        configureApiClient();

        return data;
      } catch (e) {
        console.log("error refreshing tokens", e);
        return null;
      }
    })().finally(() => {
      if (inFlightRefresh === pending) inFlightRefresh = null;
    });
    inFlightRefresh = pending;
    return pending;
  },

  forceGetUser: async (): Promise<MeOut | null> => {
    const accessToken = localStorage.getItem("access_token");
    const version = sessionVersion;
    if (!accessToken) return null;
    try {
      const { data } = await retrieveMe();
      if (!data || sessionVersion !== version) return null;
      localStorage.removeItem(LEGACY_USER_KEY);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      localStorage.setItem("last_user_update", DateTime.now().toISO() ?? "");
      return data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return null;
    }
  },

  getUser: async (): Promise<MeOut | null> => {
    if (!localStorage.getItem("access_token")) return null;
    const localUser = localStorage.getItem(USER_KEY);
    let old = null;

    if (localUser && localUser != "undefined") {
      old = JSON.parse(localUser);
    }

    const lastUserUpdate = localStorage.getItem("last_user_update");
    const old_lu = lastUserUpdate
      ? DateTime.fromISO(lastUserUpdate)
      : DateTime.now().plus({ days: -8 });

    if (old === null || old_lu < DateTime.now().plus({ hours: -12 })) {
      return await authenticationService.forceGetUser();
    } else {
      return old;
    }
  },
};

export const tokenManager = {
  decodeToken(token: string) {
    return jwtDecode<{ exp?: number }>(token);
  },
  getTokenExpirationDate(t: string) {
    const decodedToken = this.decodeToken(t);
    if (decodedToken && "exp" in decodedToken && decodedToken.exp) {
      return new Date(decodedToken.exp * 1000);
    }
    return null;
  },
  isTokenExpired(token: string, delay: number) {
    if (!token || "" === token) return true;
    const expDate = this.getTokenExpirationDate(token);
    if (!expDate) return true;
    const currentDate = new Date();
    const milliseconds = delay * 1000;
    const currentDateWithDelay = new Date(currentDate.getTime() + milliseconds);
    return currentDateWithDelay > expDate;
  },
};
